import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { getAppSettings } from '@/api/settings';
import { supabase } from '@/lib/supabaseClient';
import { useSettings } from '@/contexts/SettingsContext';

interface DebugInfo {
  context: {
    settingsFromContext: string;
    settingsFromAPI: string;
  } | null;
  directQuery: {
    data: any;
    error: string | null;
  } | null;
  rpcQuery: {
    data: any;
    error: string | null;
  } | null;
  error: string | null;
}

export function OrderConfirmation() {
  const { orderId } = useParams();
  const { settings } = useSettings();
  const [localConfirmationText, setLocalConfirmationText] = useState('');
  const [debug, setDebug] = useState<DebugInfo>({ 
    context: null, 
    directQuery: null,
    rpcQuery: null,
    error: null 
  });

  useEffect(() => {
    async function fetchData() {
      try {
        // Normal settings flow
        const appSettings = await getAppSettings();
        setLocalConfirmationText(appSettings.orderConfirmationText);
        
        // RPC query only - don't try direct table access
        const { data: rpcData, error: rpcError } = await supabase
          .rpc('get_setting', { p_key: 'order_confirmation_text' });
          
        setDebug({
          context: {
            settingsFromContext: settings.orderConfirmationText,
            settingsFromAPI: appSettings.orderConfirmationText
          },
          directQuery: {
            data: null,
            error: "Direct access not available for app_settings schema"
          },
          rpcQuery: {
            data: rpcData,
            error: rpcError ? rpcError.message : null
          },
          error: null
        });
      } catch (err) {
        console.error('Error fetching confirmation text:', err);
        setDebug(prev => ({
          ...prev,
          error: err.message || 'Unknown error'
        }));
      }
    }
    
    fetchData();
  }, [settings]);

  if (!orderId) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto text-center">
        <div className="flex justify-center">
          <CheckCircle className="h-16 w-16 text-green-600" />
        </div>
        <h1 className="mt-4 text-3xl font-extrabold text-gray-900 mb-2">
          Tack för din beställning!
        </h1>
        <p className="mt-2 text-lg text-gray-500">
          Din beställning #{orderId.slice(0, 8)} har bekräftats
        </p>
        <p className="mt-4 text-gray-500">
          {settings.orderConfirmationText || localConfirmationText}
        </p>
        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
          >
            Fortsätt handla
          </Link>
        </div>
        
        {/* Debug section - only visible in development */}
        {import.meta.env.DEV && (
          <div className="mt-12 text-left">
            <details>
              <summary className="text-gray-500 cursor-pointer">Debug Information</summary>
              <div className="mt-4 bg-gray-100 p-4 rounded overflow-auto">
                <pre>{JSON.stringify(debug, null, 2)}</pre>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}